'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'motion/react';
import { Icon } from '~/components/primitives';
import { Portal } from '~/components/primitives';
import { cx } from 'class-variance-authority';
import { forwardRef, useContext, useEffect, useState } from 'react';
import { NotificationsContext } from './useNotifications';
import type { NotificationDispatcher } from './NotificationDispatcher';
const PORTAL_ROOT_ID = 'notifications';
export default function NotificationsPortal() {
  const $ = _c(12);
  const dispatcher = useContext(NotificationsContext);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [notifications, setNotifications] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== dispatcher) {
    t1 = () => dispatcher.subscribe(newNotifications => setNotifications(newNotifications));
    t2 = [dispatcher];
    $[1] = dispatcher;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div id={PORTAL_ROOT_ID} data-testid="notifications" className="relative z-notifications" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== dispatcher || $[6] !== notifications) {
    let t5;
    if ($[8] !== dispatcher) {
      t5 = notification => <NotificationToast key={`${notification.createdAt}-${notification.data.type}-${notification.data.title}`} type={notification.data.type} title={notification.data.title} message={notification.data.message} drag="x" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onDismiss={() => dispatcher.removeNotification(notification)} onDragEnd={() => dispatcher.removeNotification(notification)} />;
      $[8] = dispatcher;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = notifications.map(t5);
    $[5] = dispatcher;
    $[6] = notifications;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <>{t3}<Portal querySelector={`#${PORTAL_ROOT_ID}`}><div className="pointer-events-none fixed inset-0 z-1 flex flex-col items-end px-4 py-6 sm:p-6"><AnimatePresence>{t4}</AnimatePresence></div></Portal></>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
const NotificationToast = motion.create(forwardRef<HTMLDivElement, NotificationDispatcher.NotificationData & {
  onDismiss: React.MouseEventHandler<HTMLButtonElement>;
}>((t0, ref) => {
  const $ = _c(19);
  const {
    type: t1,
    title,
    message,
    onDismiss
  } = t0;
  const type = t1 === undefined ? "SUCCESS" : t1;
  let t2;
  if ($[0] !== type) {
    t2 = type !== "ACTION" && <div className="shrink-0"><Icon name={(() => {
        switch (type) {
          case "SUCCESS":
            {
              return "check-circle";
            }
          case "ERROR":
            {
              return "info-circle";
            }
          case "WARNING":
            {
              return "info-circle";
            }
          default:
            {
              throw new Error(`Invalid notification type ${type}`);
            }
        }
      })()} color="none" className={cx(type === "SUCCESS" && "text-green-400", type === "ERROR" && "text-red-600", type === "WARNING" && "text-yellow-300")} /></div>;
    $[0] = type;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = title && <p className="flex w-full flex-wrap text-sm leading-5 font-medium text-gray-900">{title}</p>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== message) {
    t4 = typeof message === "string" ? <p className="text-sm leading-5 text-gray-500">{message}</p> : message;
    $[4] = message;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="flex flex-col items-center">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon name="close" size="sm" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onDismiss) {
    t7 = <button type="button" onClick={onDismiss} className="ml-auto inline-flex text-gray-400 transition duration-150 ease-in-out focus:text-gray-500 focus:outline-hidden">{t6}</button>;
    $[10] = onDismiss;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t2 || $[13] !== t5 || $[14] !== t7) {
    t8 = <div className="max-w-md overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5"><div className="relative px-6 py-4"><div className="flex items-center gap-3">{t2}{t5}{t7}</div></div></div>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== ref || $[17] !== t8) {
    t9 = <div ref={ref} className="pointer-events-auto mb-4 ml-auto flex w-full justify-end rounded-lg">{t8}</div>;
    $[16] = ref;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}));